import React, { useEffect, useState, ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { Box, CircularProgress, Typography } from '@mui/material';

interface AuthGuardProps {
  allowedRoles?: string[];
  denyRoles?: string[];
  requireAuth?: boolean;
  children?: ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  allowedRoles = [],
  denyRoles = [],
  requireAuth = true,
  children
}) => {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getToken();

      if (!token) {
        setIsAuthenticated(false);
        setHasPermission(false);
        setIsChecking(false);
        return;
      }

      setIsAuthenticated(true);

      try {
        const data = await authService.me();
        const userRole = data?.user?.role as string | undefined;
        const allowedOk = allowedRoles.length === 0 || (!!userRole && allowedRoles.includes(userRole));
        const denyOk = denyRoles.length === 0 || !userRole || !denyRoles.includes(userRole);
        setHasPermission(allowedOk && denyOk);
      } catch {
        setHasPermission(false);
      }

      setIsChecking(false);
    };

    checkAuth();
  }, [allowedRoles.join(','), denyRoles.join(','), location.pathname]);

  if (isChecking) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={60} thickness={4} color="primary" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verificando autenticação...
        </Typography>
      </Box>
    );
  }

  if (!requireAuth) {
    return children ? <>{children}</> : <Outlet />;
  }

  if (!isAuthenticated) {
    alert('Para continuar, Por favor, faça login.'); // aletar alert @Lucca
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !hasPermission) {
    return <Navigate to="/acesso-negado" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default AuthGuard;
